/**
 * The ring, as a sound.
 *
 * The desktop keeps a `.wav` in its resources (`relay/src/core/ringtone.cpp`);
 * a browser does not need one. Two sine waves and a gain envelope are the whole
 * ring, they cost nothing to download, and a file would be one more thing the
 * CSP has to allow and the service worker has to cache.
 *
 * ── Why it can be silent, and why that is not an error ──────────────────────
 * An `AudioContext` created before the page has been interacted with starts
 * suspended, and a browser is entitled to keep it that way. So `start()` asks
 * for a resume and does not check the answer: a ring that is seen but not
 * heard is the ordinary case on a tab nobody has clicked yet, and there is
 * nothing to tell anybody about it. Everything here is wrapped because
 * `AudioContext` is absent in a test and throws in a locked-down browser.
 *
 * ── The cadence ────────────────────────────────────────────────────────────
 * 440 Hz and 480 Hz together, which is the tone every telephone has used since
 * before anyone reading this was born, pulsed twice a second and then quiet for
 * three. Loud enough to notice, quiet enough to think over.
 */

/** The two tones, sounded together. */
const TONES_HZ = [440, 480];

/** One turn of the pattern: two pulses, then silence. */
const PERIOD_S = 4;
const PULSE_STARTS_S = [0, 0.6];
const PULSE_S = 0.4;
const FADE_S = 0.04;

/** Well under a system sound. A ring is an interruption, not an alarm. */
const PEAK = 0.12;

export interface RingTone {
  /** Loop until `stop()`. Calling it twice does not restart the loop. */
  start(): void;
  stop(): void;
}

type ContextCtor = new () => AudioContext;

function contextCtor(): ContextCtor | null {
  const w = globalThis as { AudioContext?: ContextCtor; webkitAudioContext?: ContextCtor };
  return w.AudioContext ?? w.webkitAudioContext ?? null;
}

export function createRingTone(): RingTone {
  let ctx: AudioContext | null = null;
  let gain: GainNode | null = null;
  let oscillators: OscillatorNode[] = [];
  let timer: number | null = null;

  /** One turn of the pattern, scheduled on the audio clock rather than a
   *  timer: a `setInterval` that drifts under load drifts audibly. */
  const pulse = (): void => {
    if (!ctx || !gain) return;
    const now = ctx.currentTime;
    gain.gain.cancelScheduledValues(now);
    gain.gain.setValueAtTime(0, now);
    for (const start of PULSE_STARTS_S) {
      const at = now + start;
      gain.gain.setValueAtTime(0, at);
      gain.gain.linearRampToValueAtTime(PEAK, at + FADE_S);
      gain.gain.setValueAtTime(PEAK, at + PULSE_S - FADE_S);
      gain.gain.linearRampToValueAtTime(0, at + PULSE_S);
    }
  };

  const stop = (): void => {
    if (timer !== null) {
      clearInterval(timer);
      timer = null;
    }
    for (const oscillator of oscillators) {
      try {
        oscillator.stop();
        oscillator.disconnect();
      } catch {
        // Already stopped, or a context that went away with the page.
      }
    }
    oscillators = [];
    gain?.disconnect();
    gain = null;
    void ctx?.close().catch(() => {});
    ctx = null;
  };

  const start = (): void => {
    if (timer !== null) return;
    const Ctor = contextCtor();
    if (!Ctor) return;
    try {
      ctx = new Ctor();
      void ctx.resume().catch(() => {});
      gain = ctx.createGain();
      gain.gain.value = 0;
      gain.connect(ctx.destination);
      for (const hz of TONES_HZ) {
        const oscillator = ctx.createOscillator();
        oscillator.type = 'sine';
        oscillator.frequency.value = hz;
        oscillator.connect(gain);
        oscillator.start();
        oscillators.push(oscillator);
      }
      pulse();
      timer = setInterval(pulse, PERIOD_S * 1000) as unknown as number;
    } catch {
      stop();
    }
  };

  return { start, stop };
}
